import { useMemo, useState } from 'react';
import { Copy, Pencil, Plus, Search, Star, Trash2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import {
  AiPrompt,
  AiPromptDraft,
  AiPromptFilters,
  useAiPromptStats,
  useAiPrompts,
  useCreateAiPrompt,
  useDeleteAiPrompt,
  useDuplicateAiPrompt,
  useSetDefaultAiPrompt,
  useToggleAiPrompt,
  useUpdateAiPrompt,
} from '../../queries/aiPrompts';
import { AiPromptFormDialog } from './AiPromptFormDialog';
import { useToast } from '../../contexts/ToastContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, DataTableColumn } from '../../components/DataTable/DataTable';
import { cn } from '../../lib/utils';

const NEW_PROMPT: AiPromptDraft = {
  prompt_name: '',
  prompt_version: '1.0',
  track_type: 'all',
  track_condition: 'all',
  day_of_week: 'all',
  track_name: 'all',
  form_weighting: 60,
  market_weighting: 40,
  system_prompt: '',
  priority: 100,
  is_active: true,
  is_default: false,
  include_historical_strike_rate: false,
};

const INPUT = 'h-9 rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

export function AiPromptsPage() {
  const [filters, setFilters] = useState<AiPromptFilters>({});
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<AiPromptDraft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AiPrompt | null>(null);

  const { data: prompts = [], isLoading } = useAiPrompts(filters);
  const { data: stats } = useAiPromptStats();
  const create = useCreateAiPrompt();
  const update = useUpdateAiPrompt();
  const del = useDeleteAiPrompt();
  const toggle = useToggleAiPrompt();
  const setDefault = useSetDefaultAiPrompt();
  const duplicate = useDuplicateAiPrompt();
  const { success, error: toastError } = useToast();

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return prompts;
    return prompts.filter((p) => p.prompt_name.toLowerCase().includes(term) || p.track_name.toLowerCase().includes(term));
  }, [prompts, search]);

  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!draft) return;
    if (!draft.prompt_name || !draft.system_prompt) {
      setError('Name and system prompt are required.');
      return;
    }
    setError(null);
    const isEdit = !!draft.id;
    const onError = (err: unknown) => setError(extractErrorMessage(err, 'Failed to save prompt.'));
    const onSuccess = () => {
      setDraft(null);
      success(isEdit ? 'Prompt saved.' : 'Prompt created.');
    };
    if (draft.id) {
      update.mutate({ id: draft.id, draft }, { onSuccess, onError });
    } else {
      create.mutate(draft, { onSuccess, onError });
    }
  };

  const handleDuplicate = (p: AiPrompt) =>
    duplicate.mutate(
      { id: p.id, new_name: `${p.prompt_name} (copy)` },
      {
        onSuccess: () => success('Prompt duplicated — open it to rename.'),
        onError: (err: unknown) => toastError(extractErrorMessage(err, 'Duplicate failed.')),
      },
    );

  const handleToggle = (p: AiPrompt) =>
    toggle.mutate(p.id, {
      onSuccess: () => success(p.is_active ? `"${p.prompt_name}" deactivated.` : `"${p.prompt_name}" activated.`),
      onError: (err: unknown) => toastError(extractErrorMessage(err, 'Update failed.')),
    });

  const handleSetDefault = (p: AiPrompt) =>
    setDefault.mutate(p.id, {
      onSuccess: () => success(`"${p.prompt_name}" is now the default.`),
      onError: (err: unknown) => toastError(extractErrorMessage(err, 'Update failed.')),
    });

  const confirmDelete = () => {
    if (!deleting) return;
    del.mutate(deleting.id, {
      onSuccess: () => {
        success(`Deleted "${deleting.prompt_name}".`);
        setDeleting(null);
      },
      onError: (err: unknown) => {
        toastError(extractErrorMessage(err, 'Delete failed.'));
        setDeleting(null);
      },
    });
  };

  const columns: DataTableColumn<AiPrompt>[] = [
    {
      key: 'name',
      header: 'Name',
      priority: true,
      render: (p) => (
        <span className="inline-flex items-center gap-1 font-medium">
          {p.prompt_name}
          {p.is_default && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />}
          <span className="text-xs font-normal text-muted-foreground">v{p.prompt_version}</span>
        </span>
      ),
    },
    { key: 'scope', header: 'Scope', priority: false, render: (p) => <span className="text-muted-foreground">{[p.track_type, p.track_condition, p.day_of_week, p.track_name].filter((x) => x !== 'all').join(' · ') || 'all'}</span> },
    { key: 'weight', header: 'Form / Market', priority: false, render: (p) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{p.form_weighting} / {p.market_weighting}</span> },
    { key: 'priority', header: 'Priority', priority: false, alignEnd: true, render: (p) => <span className="tabular-nums text-muted-foreground @2xl:text-foreground">{p.priority}</span> },
    {
      key: 'status',
      header: 'Status',
      priority: true,
      render: (p) => (
        <button
          type="button"
          onClick={() => handleToggle(p)}
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-medium transition-colors',
            p.is_active ? 'bg-emerald-500/12 text-emerald-600 hover:bg-emerald-500/20 dark:text-emerald-400' : 'bg-muted text-muted-foreground hover:bg-accent',
          )}
          title={p.is_active ? 'Click to deactivate' : 'Click to activate'}
        >
          {p.is_active ? 'Active' : 'Inactive'}
        </button>
      ),
    },
    {
      key: 'actions',
      header: '',
      priority: true,
      alignEnd: true,
      render: (p) => (
        <div className="flex items-center justify-end gap-1">
          {!p.is_default && (
            <Button variant="ghost" size="icon" onClick={() => handleSetDefault(p)} title="Set as default" className="hover:text-amber-500">
              <Star className="h-4 w-4" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => { setError(null); setDraft(p); }} title="Edit">
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => handleDuplicate(p)} title="Duplicate">
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDeleting(p)} title="Delete" className="hover:text-destructive">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="AI Prompts"
        description={
          stats
            ? `${stats.total_prompts} prompts · ${stats.active_prompts} active · ${stats.default_prompts} default`
            : 'Prompt variants that drive the tip-generation robots.'
        }
        actions={
          <Button onClick={() => { setError(null); setDraft({ ...NEW_PROMPT }); }}>
            <Plus className="h-4 w-4" />
            New prompt
          </Button>
        }
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative flex-1 sm:min-w-[16rem] sm:max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or track…" className={`${INPUT} w-full pl-9`} />
        </div>
        <select
          value={filters.is_active ?? ''}
          onChange={(e) => setFilters((f) => ({ ...f, is_active: (e.target.value || undefined) as AiPromptFilters['is_active'] }))}
          className={INPUT}
        >
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <select value={filters.track_type ?? ''} onChange={(e) => setFilters((f) => ({ ...f, track_type: e.target.value || undefined }))} className={INPUT}>
          <option value="">All track types</option>
          {['metropolitan', 'provincial', 'country', 'all'].map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <DataTable columns={columns} data={visible} getRowKey={(p) => String(p.id)} emptyMessage="No prompts match the current filters." />
      )}

      {draft && (
        <AiPromptFormDialog draft={draft} onChange={setDraft} onCancel={() => setDraft(null)} onSave={handleSave} saving={saving} error={error} />
      )}

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(open) => !open && setDeleting(null)}
        title="Delete prompt?"
        description={`"${deleting?.prompt_name}" will be permanently removed and can no longer drive tip generation. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        busy={del.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
